import axios, { AxiosInstance } from "axios";
import { z } from "zod";
import { GLOBAL_CONST } from "../../constants";

export interface CompetitionProps {
  accountId: string;
  offerId: string;
  data?: Record<string, unknown>;
}

const CompetitionResponseSchema = z
  .object({
    hasEntered: z.boolean().optional(),
    dateEntered: z.string().datetime().nullable().optional(),
    nextEntryDate: z.string().datetime().nullable().optional(),
  })
  .passthrough();

const CompetitionPutResponseSchema = z.object({
  success: z.boolean()
});

const CouponSchema = z.object({
  couponCode: z.string(),
});

const BarcodeSchema = z.object({
  driverId: z.string(),
  driverLevel: z.string(),
  driverTimeZone: z.string(),
  barcodeNumber: z.string().optional(),
  notBefore: z.number().optional(),
  notOnOrAfter: z.number().optional(),
  partnerId: z.string(),
  quotaRemaining: z.number(),
  quotaLimit: z.number().optional(),
  quotaReset: z.number().optional(),
});

const SevenElevenWarningSchema = z.object({
  status: z.literal("WARNING"),
  code: z.string(),
  message: z.string(),
});

const SevenElevenAcceptanceSchema = z.object({
  hasAccepted: z.boolean(),
});

const SevenElevenPutAcceptanceSchema = z.object({
  success: z.boolean(),
});

const RewardsSchema = z.object({
  id: z.string(),
  logoUrl: z.string(),
  subtitleText: z.string(),
  thumbnailUrl: z.string(),
  thumbnailUrlSvg: z.string(),
  titleText: z.string(),
  webviewUrl: z.string(),
  pillText: z.string().optional(),
  placard: z.boolean().optional(),
});

type Coupon = z.infer<typeof CouponSchema>;
type Barcode = z.infer<typeof BarcodeSchema>;
type SevenElevenWarning = z.infer<typeof SevenElevenWarningSchema>;
type BarcodeResult = Barcode | SevenElevenWarning;
type SevenElevenAcceptance = z.infer<typeof SevenElevenAcceptanceSchema>;
type Rewards = z.infer<typeof RewardsSchema>;
type CompetitionResponse = z.infer<typeof CompetitionResponseSchema>;
type CompetitionPutResponse = z.infer<typeof CompetitionPutResponseSchema>;

export type {
  Barcode,
  SevenElevenWarning,
  BarcodeResult,
  CompetitionResponse,
  CompetitionPutResponse,
};

export const isSevenElevenWarning = (
  result: BarcodeResult
): result is SevenElevenWarning =>
  (result as SevenElevenWarning)?.status === "WARNING";

export interface GetCouponProps {
  accountId: string;
  offerId: string;
}

export interface SevenElevenProps {
  accountId: string;
  driverLevel?: string;
}

export class RewardsHubServicesApi {
  private readonly axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: REWARDS_HUB_SERVICES_ENDPOINT,
      headers: {
        "x-api-key": REWARDS_HUB_SERVICES_API_KEY,
        Authorization: `Bearer ${GLOBAL_CONST.ACCESS_TOKEN}`,
        "Content-Type": "application/json",
      },
    });
  }

  private handleErr(err: any) {
    if (axios.isAxiosError(err)) {
      console.error("Rewards Hub Services API error:", err.response?.data || err.message);
    } else {
      console.error("Rewards Hub Services unexpected error:", err);
    }
  }

  public async getRewards(id: string): Promise<Rewards> {
    try {
      const res = await this.axiosInstance.get("/v1/rewards", { params: { id } });
      return RewardsSchema.parse(res.data);
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async getCoupon(props: GetCouponProps): Promise<Coupon> {
    try {
      const res = await this.axiosInstance.get("/v1/coupon", { params: { ...props } });
      return CouponSchema.parse(res.data);
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async getCompetition(props: CompetitionProps): Promise<boolean> {
    try {
      const res = await this.axiosInstance.get('/v1/competition', {
        params: props,
      });
      const result = CompetitionResponseSchema.parse(res.data);
      const hasEnteredFlag =
        (typeof result.hasEntered === "boolean" && result.hasEntered) ||
        Boolean(result.nextEntryDate);

      return hasEnteredFlag;
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async getBarcode(props: SevenElevenProps): Promise<BarcodeResult> {
    try {
      const requestBody = props.driverLevel
        ? { driverLevel: props.driverLevel }
        : {};
      const { data } = await this.axiosInstance.post(
        `/v1/seven-eleven/${props.accountId}/get-barcode`,
        requestBody
      );
      const barcodeData = data.body ?? data;
      if (barcodeData?.status === "WARNING") {
        return SevenElevenWarningSchema.parse(barcodeData);
      }
      return BarcodeSchema.parse(barcodeData);
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async getSevenElevenAcceptance(
    props: SevenElevenProps
  ): Promise<SevenElevenAcceptance> {
    try {
      const { data } = await this.axiosInstance.get(
        `/v1/seven-eleven/${props.accountId}/accept`
      );
      return SevenElevenAcceptanceSchema.parse(data);
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async putCompetition(props: CompetitionProps): Promise<boolean> {
    try {
      const res = await this.axiosInstance.put('/v1/competition', props);
      const result = CompetitionPutResponseSchema.parse(res.data);
      return result.success;
    } catch (err) {
      this.handleErr(err);
      throw err;
    }
  }

  public async putSevenElevenAcceptance(
    props: SevenElevenProps
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(
        `/v1/seven-eleven/${props.accountId}/accept`
      );
      const result = SevenElevenPutAcceptanceSchema.parse(data);
      return result.success;
    } catch (err) {
      this.handleErr(err);
      return false;
    }
  }
}